import { gql } from '@apollo/client';

import axiosInstance from '@/api/axiosInstance';

export enum FinancialAssociationEntityType {
  EVENT = 'EVENT',
  SPONSOR = 'SPONSOR',
  MEMBERSHIPTYPE = 'MEMBERSHIPTYPE',
  OTHERITEM = 'OTHERITEM',
}

export interface FinancialAssociation {
  id: string;
  tenantId: string;
  entityId: string;
  entityType: FinancialAssociationEntityType;
  chartOfAccountId: string;
  accountClassId: string;
  createdByUserId: string;
  updatedByUserId: string;
  createdAt: string;
  updatedAt: string;
}

export interface FinancialAssociationResponse {
  status: boolean;
  message?: string;
  data?: FinancialAssociation | null;
}

export interface CreateFinancialAssociationInput {
  tenantId: string | undefined;
  entityId: string;
  entityType: FinancialAssociationEntityType;
  chartOfAccountId: string | null | undefined;
  accountClassId: string | null | undefined;
  createdByUserId: string | undefined;
  updatedByUserId: string | undefined;
}

export interface UpdateFinancialAssociationInput {
  tenantId: string | undefined;
  entityId: string;
  entityType: FinancialAssociationEntityType;
  chartOfAccountId: string | null | undefined;
  accountClassId: string | null | undefined;
  updatedByUserId: string | undefined;
}

export interface getAssociationsResponse {
  status: boolean;
  message?: string;
  data?: getAssociationsResponseData | null;
}

export interface getAssociationsResponseData {
  membershipTypeId: string | null;
  eventId: string | null;
}

export async function createFinancialAssociation(
  input: CreateFinancialAssociationInput
): Promise<FinancialAssociationResponse> {
  const response = await axiosInstance.post(
    '/api/v1/financial-settings/association',
    input,
    {
      headers: { 'x-tenant-id': input.tenantId },
    }
  );
  return response.data;
}

export async function updateFinancialAssociation(
  input: UpdateFinancialAssociationInput
): Promise<FinancialAssociationResponse> {
  const response = await axiosInstance.post(
    '/api/v1/financial-settings/association',
    input,
    {
      headers: { 'x-tenant-id': input.tenantId },
    }
  );
  return response.data;
}

export async function getFinancialAssociation({
  tenantId,
  entityId,
}: {
  tenantId: string;
  entityId: string;
}): Promise<FinancialAssociationResponse> {
  try {
    const response = await axiosInstance.get(
      `/api/v1/financial-settings/association`,
      {
        headers: { 'x-tenant-id': tenantId },
        params: {
          tenantId,
          entityId,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return {
        data: null,
        status: false,
        message: 'Financial settings not found',
      };
    }
    throw error;
  }
}

export async function getChartOfAccountAssociations({
  tenantId,
  chartofAccountId,
}: {
  tenantId: string;
  chartofAccountId: string;
}): Promise<getAssociationsResponse> {
  try {
    const response = await axiosInstance.get(
      `/api/v1/financial-settings/association/getChartOfAccountAssociations`,
      {
        headers: { 'x-tenant-id': tenantId },
        params: {
          tenantId,
          chartofAccountId,
        },
      }
    );
    return response.data ;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return {
        data: null,
        status: false,
        message: 'No associations found',
      };
    }
    throw error;
  }
}

export async function getAccountClassAssociations({
  tenantId,
  accountClassId,
}: {
  tenantId: string;
  accountClassId: string;
}): Promise<getAssociationsResponse> {
  try {
    const response = await axiosInstance.get(
      `/api/v1/financial-settings/association/getAccountClassAssociations`,
      {
        headers: { 'x-tenant-id': tenantId },
        params: {
          tenantId,
          accountClassId,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return {
        data: null,
        status: false,
        message: 'No associations found',
      };
    }
    throw error;
  }
}
